import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

type Status = "pending" | "success" | "error";

/**
 * Post-Cognito-Hosted-UI redirect target.
 *
 * Amplify completes the OAuth code exchange asynchronously when this page
 * mounts. We listen on the Hub for ``signedIn`` (success) and
 * ``signInWithRedirect_failure`` (error), and also probe
 * ``fetchAuthSession`` directly in case the event already fired before we
 * subscribed.
 *
 * Once authed, we navigate to ``/projects`` (the post-Stage-7 landing).
 * AuthProvider's Hub listener will pick up the same ``signedIn`` event and
 * load /api/auth/me in parallel — by the time the user lands on /projects
 * the session is hydrated.
 */
export function AuthCallback() {
  const [status, setStatus] = useState<Status>("pending");

  useEffect(() => {
    let cancelled = false;

    const finish = (next: Status) => {
      if (cancelled) return;
      setStatus(next);
    };

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn") {
        finish("success");
      } else if (payload.event === "signInWithRedirect_failure") {
        finish("error");
      }
    });

    const probe = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          finish("success");
        }
      } catch {
        // No session yet — that's fine, the Hub listener will fire when
        // Amplify finishes the OAuth code exchange.
      }
    };
    void probe();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (status === "success") {
    return <Navigate to="/projects" replace />;
  }

  if (status === "error") {
    // Surface a clean, human message — no raw OAuth error codes.
    return (
      <main style={styles.viewport}>
        <div style={styles.card}>
          <h1 style={styles.title}>Sign-in didn't complete</h1>
          <p style={styles.body}>
            Something went wrong returning from your identity provider. Please
            try again from the home page.
          </p>
          <a href="/" style={styles.link}>
            Return home
          </a>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.viewport}>
      <div style={styles.card}>
        <div role="status" style={styles.statusText}>
          Finishing sign-in…
        </div>
      </div>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  viewport: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 1rem",
    background: "#fafafa",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "#ffffff",
    borderRadius: 12,
    padding: "2rem",
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.04), 0 6px 12px -3px rgba(50, 50, 93, 0.08)",
    textAlign: "center",
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: 600,
    letterSpacing: "-0.015em",
    margin: "0 0 0.75rem",
    color: "#0a0e27",
  },
  body: {
    fontSize: "0.9375rem",
    color: "#525f7f",
    margin: "0 0 1.25rem",
    lineHeight: 1.5,
  },
  statusText: {
    fontSize: "0.9375rem",
    color: "#525f7f",
  },
  link: {
    color: "#635bff",
    fontWeight: 500,
    fontSize: "0.9375rem",
  },
};
